import { useEffect, useState, useRef } from 'react'
import { supabase } from '../lib/supabase'
import { useAuth } from '../context/AuthContext'

function timeAgo(date) {
  const diff = (new Date() - new Date(date)) / 1000
  if (diff < 60) return 'just now'
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`
  return new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

function Avatar({ name, size = 36 }) {
  const initials = (name || '?').split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase()
  return (
    <div style={{
      width: size, height: size, borderRadius: '50%', flexShrink: 0,
      background: '#202326',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      color: '#CBB17B', fontWeight: 700, fontSize: size * 0.33
    }}>{initials}</div>
  )
}

export default function MessagingPage() {
  const { profile, user, isCPA } = useAuth()
  const [clients, setClients] = useState([])
  const [selectedClient, setSelectedClient] = useState(null)
  const [messages, setMessages] = useState([])
  const [newMessage, setNewMessage] = useState('')
  const [sending, setSending] = useState(false)
  const [loading, setLoading] = useState(true)
  const [unreadCounts, setUnreadCounts] = useState({})
  const bottomRef = useRef()
  const inputRef = useRef()

  useEffect(() => {
    if (!profile?.id) return
    loadClients()
  }, [profile])

  useEffect(() => {
    if (selectedClient) {
      loadMessages(selectedClient.id)
      markAsRead(selectedClient.id)

      // Subscribe to real-time messages
      const channel = supabase
        .channel(`messages-${selectedClient.id}`)
        .on('postgres_changes', {
          event: 'INSERT',
          schema: 'public',
          table: 'messages',
          filter: `client_id=eq.${selectedClient.id}`
        }, payload => {
          setMessages(prev => {
            if (prev.find(m => m.id === payload.new.id)) return prev
            return [...prev, payload.new]
          })
          markAsRead(selectedClient.id)
        })
        .subscribe()

      return () => supabase.removeChannel(channel)
    }
  }, [selectedClient])

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  async function loadClients() {
    if (isCPA) {
      const { data: c } = await supabase.from('clients').select('*').eq('cpa_id', profile.id).order('full_name')
      setClients(c || [])

      // Get unread counts
      if (c && c.length > 0) {
        const { data: unread } = await supabase
          .from('messages')
          .select('client_id')
          .in('client_id', c.map(x => x.id))
          .eq('sender_role', 'client')
          .is('read_at', null)
        
        const counts = {}
        ;(unread || []).forEach(m => {
          counts[m.client_id] = (counts[m.client_id] || 0) + 1
        })
        setUnreadCounts(counts)
      }
    } else {
      // Client — find their own record
      const { data: client } = await supabase.from('clients').select('*').eq('portal_user_id', profile?.id || user?.id).maybeSingle()
      if (client) {
        setClients([client])
        setSelectedClient(client)
      }
    }
    setLoading(false)
  }

  async function loadMessages(clientId) {
    const { data } = await supabase
      .from('messages')
      .select('*')
      .eq('client_id', clientId)
      .order('created_at', { ascending: true })
    setMessages(data || [])
  }

  async function markAsRead(clientId) {
    const senderRole = isCPA ? 'client' : 'cpa'
    await supabase
      .from('messages')
      .update({ read_at: new Date().toISOString() })
      .eq('client_id', clientId)
      .eq('sender_role', senderRole)
      .is('read_at', null)

    await supabase.from('messages')
      .update({ read: true })
      .eq('client_id', clientId)
      .eq('sender', senderRole)
      .is('read_at', null)
    
    setUnreadCounts(prev => ({ ...prev, [clientId]: 0 }))
  }

  async function sendMessage(e) {
    e.preventDefault()
    if (!newMessage.trim() || !selectedClient) return
    setSending(true)

    const { data } = await supabase.from('messages').insert({
      client_id: selectedClient.id,
      cpa_id: isCPA ? profile.id : selectedClient.cpa_id,
      sender: isCPA ? 'cpa' : 'client',
      sender_role: isCPA ? 'cpa' : 'client',
      body: newMessage.trim(),
      content: newMessage.trim(),
    }).select().single()

    if (data) {
      setMessages(prev => [...prev, data])
      setNewMessage('')
    }
    setSending(false)
    inputRef.current?.focus()
  }

  if (loading) return (
    <div style={{ display: 'grid', gap: 10 }}>
      {[...Array(3)].map((_, i) => <div key={i} className="skeleton" style={{ height: 64 }} />)}
    </div>
  )

  return (
    <div className="messaging-page-root" style={{ display: 'grid', gridTemplateColumns: isCPA ? '280px 1fr' : '1fr', gap: 0, height: 'calc(100dvh - 150px)', background: '#fff', borderRadius: 12, overflow: 'hidden', border: '1px solid var(--border)' }}>
      <style>{`
        @media (max-width: 768px) {
          .messaging-page-root {
            grid-template-columns: 1fr !important;
            height: calc(100vh - 94px) !important;
          }
          .messaging-page-root .message-client-list {
            max-height: 34vh;
            border-right: none !important;
            border-bottom: 1px solid var(--border);
          }
          .messaging-page-root .message-chat-shell {
            min-height: 0;
          }
          .messaging-page-root .message-bubble {
            max-width: 88% !important;
          }
        }
      `}</style>
      
      {/* Client list (CPA only) */}
      {isCPA && (
        <div className="message-client-list" style={{ borderRight: '1px solid var(--border)', overflow: 'auto' }}>
          <div style={{ padding: '16px 16px 12px', borderBottom: '1px solid var(--border)', background: '#F7F3EB' }}>
            <h3 style={{ fontSize: 14, fontWeight: 700, color: '#202326' }}>Messages</h3>
          </div>
          {clients.length === 0 ? (
            <div style={{ padding: 24, textAlign: 'center', color: '#62615D', fontSize: 13 }}>No clients yet</div>
          ) : clients.map(c => (
            <div key={c.id} role="button" tabIndex={0} onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setSelectedClient(c) } }}
              onClick={() => setSelectedClient(c)}
              style={{
                padding: '12px 14px', display: 'flex', alignItems: 'center', gap: 10,
                cursor: 'pointer', borderBottom: '1px solid #F7F3EB', transition: 'background 0.1s',
                background: selectedClient?.id === c.id ? '#F1E8D7' : 'transparent',
                borderLeft: selectedClient?.id === c.id ? '3px solid #CBB17B' : '3px solid transparent'
              }}
              onMouseEnter={e => { if (selectedClient?.id !== c.id) e.currentTarget.style.background = '#FAFAF7' }}
              onMouseLeave={e => { if (selectedClient?.id !== c.id) e.currentTarget.style.background = 'transparent' }}
            >
              <Avatar name={c.business_name || c.full_name} size={36} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#202326', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {c.business_name || c.full_name}
                </div>
                <div style={{ fontSize: 11, color: '#62615D' }}>{c.entity_type?.toUpperCase()}</div>
              </div>
              {unreadCounts[c.id] > 0 && (
                <div style={{ width: 20, height: 20, borderRadius: '50%', background: '#CBB17B', color: '#202326', fontSize: 11, fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  {unreadCounts[c.id]}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Chat area */}
      {!selectedClient ? (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 12, color: '#62615D' }}>
          <div style={{ fontSize: 40 }}>💬</div>
          <div style={{ fontSize: 14, fontWeight: 600 }}>Select a client to start messaging</div>
          <div style={{ fontSize: 12 }}>Choose a client from the list on the left</div>
        </div>
      ) : (
        <div className="message-chat-shell" style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          {/* Chat header */}
          <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--border)', background: '#F7F3EB', display: 'flex', alignItems: 'center', gap: 10 }}>
            <Avatar name={selectedClient.business_name || selectedClient.full_name} size={36} />
            <div>
              <div style={{ fontSize: 14, fontWeight: 700, color: '#202326' }}>{selectedClient.business_name || selectedClient.full_name}</div>
              <div style={{ fontSize: 11, color: '#62615D' }}>{selectedClient.email}</div>
            </div>
          </div>

          {/* Messages */}
          <div style={{ flex: 1, overflow: 'auto', padding: '20px 20px 8px' }}>
            {messages.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '48px 0', color: '#62615D' }}>
                <div style={{ fontSize: 32, marginBottom: 8 }}>💬</div>
                <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 4 }}>No messages yet</div>
                <div style={{ fontSize: 12 }}>Start the conversation below</div>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {messages.map((msg, idx) => {
                  const isMe = isCPA ? msg.sender_role === 'cpa' : msg.sender_role === 'client'
                  const showDate = idx === 0 || new Date(msg.created_at).toDateString() !== new Date(messages[idx - 1].created_at).toDateString()
                  return (
                    <div key={msg.id}>
                      {showDate && (
                        <div style={{ textAlign: 'center', fontSize: 11, color: '#62615D', margin: '8px 0', display: 'flex', alignItems: 'center', gap: 8 }}>
                          <div style={{ flex: 1, height: 1, background: '#F0EBE0' }} />
                          {new Date(msg.created_at).toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })}
                          <div style={{ flex: 1, height: 1, background: '#F0EBE0' }} />
                        </div>
                      )}
                      <div style={{ display: 'flex', justifyContent: isMe ? 'flex-end' : 'flex-start', gap: 8, alignItems: 'flex-end' }}>
                        {!isMe && <Avatar name={selectedClient.business_name || selectedClient.full_name} size={28} />}
                        <div className="message-bubble" style={{ maxWidth: '70%' }}>
                          <div style={{
                            padding: '10px 14px', borderRadius: isMe ? '14px 14px 4px 14px' : '14px 14px 14px 4px',
                            background: isMe ? '#202326' : '#F7F3EB',
                            color: isMe ? '#fff' : '#202326',
                            fontSize: 14, lineHeight: 1.5
                          }}>
                            {msg.body || msg.content}
                          </div>
                          <div style={{ fontSize: 10, color: '#62615D', marginTop: 3, textAlign: isMe ? 'right' : 'left' }}>
                            {timeAgo(msg.created_at)}
                            {isMe && msg.read_at && <span style={{ marginLeft: 4 }}>✓✓</span>}
                          </div>
                        </div>
                        {isMe && <Avatar name={profile.full_name} size={28} />}
                      </div>
                    </div>
                  )
                })}
                <div ref={bottomRef} />
              </div>
            )}
          </div>

          {/* Message input */}
          <form onSubmit={sendMessage} style={{ padding: '12px 16px', borderTop: '1px solid var(--border)', display: 'flex', gap: 10, alignItems: 'flex-end', background: '#fff' }}>
            <textarea
              ref={inputRef}
              value={newMessage}
              onChange={e => setNewMessage(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(e) } }}
              placeholder="Type a message… (Enter to send, Shift+Enter for new line)"
              rows={1}
              style={{
                flex: 1, padding: '10px 14px', borderRadius: 10,
                border: '1.5px solid var(--border)', fontSize: 14,
                fontFamily: "'DM Sans', sans-serif", resize: 'none',
                outline: 'none', lineHeight: 1.5, maxHeight: 120, overflow: 'auto',
                transition: 'border-color 0.15s'
              }}
              onFocus={e => e.target.style.borderColor = '#CBB17B'}
              onBlur={e => e.target.style.borderColor = 'var(--border)'}
            />
            <button type="submit" disabled={sending || !newMessage.trim()} style={{
              width: 42, height: 42, borderRadius: 10, border: 'none', cursor: 'pointer',
              background: newMessage.trim() ? '#202326' : '#E8E2D9',
              color: newMessage.trim() ? '#CBB17B' : '#62615D',
              fontSize: 18, display: 'flex', alignItems: 'center', justifyContent: 'center',
              transition: 'all 0.15s', flexShrink: 0
            }}>
              {sending ? '…' : '↑'}
            </button>
          </form>
        </div>
      )}
    </div>
  )
}
